import { ChevronRight, Grid2x2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { useGoogleCategoriesStore, type RootGoogleCategoryTree } from "~/lib/stores/google-categories";
import { useNavigate } from "react-router";
import type { GoogleCategoryTree } from "~/api/deal";
import { useState } from "react";

export interface CategoriesDropdownProps {
  onClick: () => void;
  showAnnouncement?: boolean;
}

export default function CategoriesDropdown({ onClick, showAnnouncement = false }: CategoriesDropdownProps) {
  const navigate = useNavigate();
  const { rootCategories, isLoading, getSubcategories } = useGoogleCategoriesStore();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);

  const selectedRootCategory = rootCategories.find(cat => cat.categoryId === selectedCategoryId);
  const currentSubcategories = selectedCategoryId ? getSubcategories(selectedCategoryId) || [] : [];

  const handleCategorySelect = (category: GoogleCategoryTree) => {
    setSelectedCategoryId(category.categoryId);
  };

  const handleSubcategoryClick = (rootCategory: RootGoogleCategoryTree | undefined, categoryId: string) => {
    navigate(`/shop/category?categoryId=${encodeURIComponent(categoryId)}`);
    onClick();
  };

  const handleViewAllClick = (rootCategory: RootGoogleCategoryTree) => {
    if (rootCategory.categoryId) {
      navigate(`/shop/category?categoryId=${encodeURIComponent(rootCategory.categoryId)}`);
      onClick();
    }
  };

  return (
    <div
      id="categories-dropdown"
      className={cn(
        "fixed z-52 flex overflow-hidden rounded-[24px] border border-white bg-white",
        "left-[16px] w-[calc(100vw-32px)] max-w-[1024px] lg:left-[146px] lg:w-[1024px]",
        showAnnouncement ? "top-[125px]" : "top-[93px]"
      )}
    >
      <div className="flex h-full w-[254px] flex-col overflow-y-auto">
        {isLoading && rootCategories.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-[14px] text-neutral-500">Loading...</p>
          </div>
        ) : (
          rootCategories.map((category, index) => (
            <button
              key={category.categoryId}
              onMouseEnter={() => handleCategorySelect(category)}
              className={`flex h-[44px] w-full items-center justify-between px-[16px] py-[12px] ${
                selectedCategoryId === category.categoryId
                  ? "bg-white"
                  : index % 2 === 0
                    ? "bg-[#f9f9f9]"
                    : "bg-[#f9f9f9]"
              }`}
            >
              <span className="text-[14px] leading-[20px] font-medium text-neutral-900">
                {category.category || "Unknown"}
              </span>
              <ChevronRight className="size-[16px] shrink-0 text-[#d4d4d4]" />
            </button>
          ))
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[16px] p-[16px]">
        {isLoading && currentSubcategories.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-[14px] text-neutral-500">Loading subcategories...</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-[8px] lg:grid-cols-5">
            {selectedRootCategory && (
              <div
                onClick={() => handleViewAllClick(selectedRootCategory)}
                className="flex cursor-pointer flex-col items-center gap-[8px] hover:opacity-80"
              >
                <div className="flex size-[64px] items-center justify-center rounded-[16px] border border-[#dddddd]">
                  <Grid2x2 className="size-[24px] text-neutral-900" />
                </div>
                <p className="text-center text-[12px] leading-[18px] font-medium text-neutral-700">View all</p>
              </div>
            )}

            {currentSubcategories.map(subcategory => (
              <div
                key={subcategory.categoryId}
                onClick={() => handleSubcategoryClick(selectedRootCategory, subcategory.categoryId)}
                className="flex cursor-pointer flex-col items-center gap-[8px] hover:opacity-80"
              >
                <div className="flex size-[64px] items-center justify-center overflow-hidden rounded-[16px] border border-[#dddddd]">
                  {subcategory.imageUrl ? (
                    <img
                      src={subcategory.imageUrl}
                      alt={subcategory.category || ""}
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="size-full bg-gray-100" />
                  )}
                </div>
                <p className="text-center text-[12px] leading-[18px] font-medium text-neutral-700">
                  {subcategory.category || "Unknown"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
